import React, { useEffect, useRef, useState } from "react";
import { Filter as FilterIcon, RefreshCw, Clock, PlaneTakeoff, X } from "lucide-react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { StopsFilter } from "./routeSearchHelpers";
import FilterSortIcon from "assets/images/FilterSortIcon.svg";

export type DurationSortOrder = "asc" | "desc";

interface FilterSortBarProps {
  stopsFilter: StopsFilter;
  onStopsFilterChange: (filter: StopsFilter) => void;
  hideConnectedFilter?: boolean;
  /** Hide the Filter (Stops) control, e.g. when it's already shown elsewhere. */
  hideFilter?: boolean;
  /** Smaller Sort button, for placing inline with a results header instead of the search-field row. */
  compact?: boolean;
  sortBy?: string | null;
  sortOrder: DurationSortOrder | null;
  onSortOrderChange: (order: DurationSortOrder | null, sortBy?: string | null) => void;
  /** Sort only makes sense once there are results to sort — hide it until then. */
  showSort?: boolean;
  /** Bumped by a parent (e.g. "Widen search") to force the Filter dropdown open. */
  openFilterSignal?: number;
  displayedResultCount?: number;
  className?: string;
}

const STOPS_OPTIONS: {
  value: Exclude<StopsFilter, null>;
  label: string;
  connectedOnly?: boolean;
}[] = [
  { value: "none", label: "Non-stop" },
  { value: "one", label: "1 Stop" },
  { value: "two", label: "2 Stops" },
  { value: "one-or-fewer", label: "Connected", connectedOnly: true },
];

const STOPS_LABELS: Record<string, string> = {
  none: "Non-stop",
  "one-or-fewer": "Connected",
  one: "1 Stop",
  two: "2 Stops",
  three: "3 Stops",
};

export const SORT_CATEGORIES = [
  {
    key: "departureTime",
    label: "Departure Time",
    icon: "st-arrow-slant",
    ascLabel: "Earliest Departure",
    descLabel: "Latest Departure",
    selectable: true,
  },
  {
    key: "duration",
    label: "Duration",
    icon: "st-time",
    ascLabel: "Shortest Duration",
    descLabel: "Longest Duration",
    selectable: true,
  },
  // Not shown in the manual Sort By menu below — only reachable via AI Assist chips.
  // Kept here so the Sort: <label> badge shows the correct text instead of falling
  // back to the Departure Time labels for these sortBy keys.
  {
    key: "arrival",
    label: "Arrival Time",
    icon: "st-arrow-slant",
    ascLabel: "Earliest Arrival",
    descLabel: "Latest Arrival",
  },
  {
    key: "layover",
    label: "Layover",
    icon: "st-time",
    ascLabel: "Shortest Layover",
    descLabel: "Longest Layover",
  },
  {
    key: "stops",
    label: "Stops",
    icon: "st-time",
    ascLabel: "Fewest Stops",
    descLabel: "Most Stops",
  },
];

// Filter (Stop) + Sort (Duration & Departure Time) controls, shown in the search form's
// toggle row so a filter/sort can be chosen before the first search, and
// changed afterwards to instantly re-filter/re-sort the results.
const FilterSortBar: React.FC<FilterSortBarProps> = ({
  stopsFilter,
  onStopsFilterChange,
  hideConnectedFilter = false,
  hideFilter = false,
  compact = false,
  sortBy,
  sortOrder,
  onSortOrderChange,
  showSort = true,
  openFilterSignal,
  displayedResultCount,
  className,
}) => {
  const [openMenu, setOpenMenu] = useState<"filter" | "sort" | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setOpenMenu(null));

  useEffect(() => {
    if (openFilterSignal !== undefined) setOpenMenu("filter");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFilterSignal]);

  const currentSortBy = sortBy || "duration";
  const matchedSortCategory = SORT_CATEGORIES.find(
    (c) => c.key === currentSortBy || (c.key === "departureTime" && currentSortBy === "departure")
  );
  // Only the two categories in the manual Sort By menu (Departure Time, Duration) get a
  // "Sort: <label>" badge. AI-Assist-only sortBy values (arrival, layover, stops) have no
  // matching menu entry to highlight, so show the plain default "Sort By" text for them too.
  const isSortActive = !!sortOrder && !!matchedSortCategory?.selectable;
  const isFilterActive = !!stopsFilter && stopsFilter !== "none";
  const activeLabel = matchedSortCategory
    ? sortOrder === "desc"
      ? matchedSortCategory.descLabel
      : matchedSortCategory.ascLabel
    : null;

  const options = STOPS_OPTIONS.filter(
    (o) => !(o.connectedOnly && hideConnectedFilter),
  );

  return (
    <div
      className={`flex items-center gap-3 ${className ?? ""}`}
      ref={containerRef}
    >
      {/* Filter (Stop) */}
      {!hideFilter && (
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpenMenu((m) => (m === "filter" ? null : "filter"))}
          className={`flex items-center gap-2 px-4 h-[52px] sm:h-[56px] border rounded-xl text-sm sm:text-base font-manrope font-medium transition-colors ${
            isFilterActive
              ? "border-[#FFBC00] bg-[#FFFBEB] text-[#92400E]"
              : "border-[#ECECED] bg-white text-[#192227] hover:bg-gray-50"
          }`}
        >
          <FilterIcon className="w-[18px] h-[18px]" />
          {isFilterActive
            ? `Filter: ${STOPS_LABELS[stopsFilter as string]}`
            : "Filter"}
        </button>

        {openMenu === "filter" && (
          <div className="absolute left-0 z-50 mt-1.5 w-[190px] bg-white rounded-xl shadow-xl border border-gray-200/60 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
            {/* Dark Header */}
            <div className="flex items-center justify-between bg-[#1C242B] px-4 py-2.5">
              <h3 className="text-white text-[15px] font-semibold tracking-tight">
                Filters
              </h3>
              <button
                type="button"
                onClick={() => {
                  onStopsFilterChange("none");
                  setOpenMenu(null);
                }}
                className="text-white hover:opacity-80 transition-opacity p-0.5 rounded-full focus:outline-none"
                title="Reset filters"
                aria-label="Reset filters"
              >
                <RefreshCw className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Body Content */}
            <div className="px-4 pt-2.5 pb-3">
              {displayedResultCount !== undefined && (
                <p className="text-[#717D96] text-xs font-normal mb-1.5">
                  Showing {displayedResultCount} {displayedResultCount === 1 ? "result" : "results"}
                </p>
              )}

              <p className="text-[#111827] text-[13px] font-bold mb-2">
                Stops
              </p>

              <div className="flex flex-col gap-2">
                {options.map((o) => {
                  const isSelected = stopsFilter === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        onStopsFilterChange(o.value);
                        setOpenMenu(null);
                      }}
                      className="flex items-center gap-2 py-0.5 text-left group cursor-pointer transition-colors"
                    >
                      <span
                        className={`flex-shrink-0 w-[18px] h-[18px] rounded-full border-[1.8px] flex items-center justify-center transition-all bg-white ${
                          isSelected
                            ? "border-black"
                            : "border-[#9CA3AF] group-hover:border-gray-500"
                        }`}
                      >
                        {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#FFBC00]" />}
                      </span>
                      <span
                        className={`text-sm transition-colors ${
                          isSelected
                            ? "text-black font-bold"
                            : "text-[#111827] font-semibold group-hover:text-black"
                        }`}
                      >
                        {o.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Sort By — hidden until there are results to sort */}
      {showSort && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenMenu((m) => (m === "sort" ? null : "sort"))}
            className={`flex items-center gap-1.5 font-medium transition-colors ${
              compact
                ? "px-3 h-8 rounded-lg text-xs"
                : "px-4 h-[52px] sm:h-[56px] rounded-xl text-sm"
            } border ${
              isSortActive
                ? "border-[#FFBC00] bg-[#FFFBEB] text-[#92400E]"
                : "border-[#ECECED] bg-white text-[#192227] hover:bg-gray-50"
            }`}
          >
            <img className="w-4" src={FilterSortIcon} alt="sort icon" />
            {isSortActive
              ? `Sort: ${activeLabel}`
              : "Sort By"}
            {isSortActive && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onSortOrderChange(null, null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    onSortOrderChange(null, null);
                  }
                }}
                className="ml-0.5 p-0.5 rounded-full hover:bg-black/10 transition-colors cursor-pointer"
                title="Clear sort"
                aria-label="Clear sort"
              >
                <X className="w-3.5 h-3.5 text-[#92400E]" />
              </span>
            )}
          </button>

          {openMenu === "sort" && (
            <div
              className={`z-50 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-200 w-56 max-w-[calc(100vw-2rem)] absolute mt-2 top-full ${
                compact ? "right-0" : "left-0"
              } origin-top-right overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150`}
            >
              <ul className="px-4 text-sm text-gray-700 pb-2">
                {/* 🔹 Sort By Title with Clickable Sorting Icon */}
                <p className="py-4 border-b border-gray-200 font-semibold text-gray-700 flex justify-between items-center text-sm">
                  Sort By
                  {isSortActive && (
                    <button
                      type="button"
                      onClick={() => {
                        const newOrder = sortOrder === "asc" ? "desc" : "asc";
                        onSortOrderChange(newOrder, currentSortBy);
                      }}
                      className="focus:outline-none cursor-pointer"
                      title={`Toggle order (${sortOrder === "asc" ? "Ascending" : "Descending"})`}
                    >
                      <img
                        src={`/images/sort-${sortOrder || "asc"}.svg`}
                        alt={sortOrder === "asc" ? "Ascending" : "Descending"}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </button>
                  )}
                </p>

                {/* Sort options (User's two options: Departure Time and Duration) */}
                {SORT_CATEGORIES.filter((c) => c.selectable).map(({ key, icon, label }) => {
                  const isSelected =
                    (currentSortBy === key || (key === "departureTime" && currentSortBy === "departure")) &&
                    isSortActive;
                  return (
                    <li
                      key={key}
                      onClick={() => {
                        if (isSelected) {
                          // Toggle order if clicking the already selected option
                          const newOrder = sortOrder === "asc" ? "desc" : "asc";
                          onSortOrderChange(newOrder, key);
                        } else {
                          // Default to ascending when selecting a new option
                          onSortOrderChange("asc", key);
                        }
                        setOpenMenu(null);
                      }}
                      className="flex items-center gap-2 py-2.5 px-3 cursor-pointer group hover:bg-gray-50/70 rounded-md transition-colors"
                    >
                      <i
                        className={`${icon} text-sm ${
                          isSelected ? "text-[#FFBC00]" : "text-lightgray-150 group-hover:text-gray-500"
                        }`}
                      ></i>
                      <p
                        className={`text-sm ${
                          isSelected
                            ? "text-[#FFBC00] font-semibold"
                            : "text-lightgray-200 group-hover:text-gray-800"
                        }`}
                      >
                        {label}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterSortBar;
